import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider, 
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import VerifiedIcon from "@mui/icons-material/Verified";
import GavelIcon from "@mui/icons-material/Gavel";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import SecurityIcon from "@mui/icons-material/Security";
import PublicIcon from "@mui/icons-material/Public";

const points = [
  {
    title: "Privacy & data handling",
    icon: <PrivacyTipIcon />,
    desc:
      "We collect only information voluntarily shared by you (e.g., email/phone/message when you contact us). We do not sell personal information.",
  },
  {
    title: "No tracking / analytics",
    icon: <PublicIcon />,
    desc:
      "We do not use analytics or advertising trackers to monitor user behaviour. Our site functions without non-essential cookies.",
  },
  {
    title: "Security",
    icon: <SecurityIcon />,
    desc:
      "We use HTTPS and reasonable safeguards to protect information. No online system is 100% secure, but we take practical measures to reduce risk.",
  },
  {
    title: "Terms & acceptable use",
    icon: <GavelIcon />,
    desc:
      "Website usage is governed by our Terms of Service. Please use the site lawfully and avoid misuse or unauthorized access attempts.",
  },
];

const LegalCompliance = () => {
  const lastUpdated = "December 16, 2025";

  return (
    <Box component="main">
      {/* HERO */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: 980, mx: "auto" }}>
            <Typography variant="h4" component="h1" fontWeight={900} gutterBottom>
              Legal & Compliance
            </Typography>
            <Typography variant="h6" color="text.secondary">
              A quick summary of how we handle privacy, website usage, and compliance.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="center"
              sx={{ mt: 3 }}
            >
              <Button variant="contained" component={RouterLink} to="/privacy-policy">
                Privacy Policy
              </Button>
              <Button variant="outlined" component={RouterLink} to="/terms-of-service">
                Terms of Service
              </Button>
              <Button variant="outlined" component={RouterLink} to="/contact-us">
                Contact Us
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }} useFlexGap flexWrap="wrap">
              <Chip icon={<VerifiedIcon />} label={`Last updated: ${lastUpdated}`} />
              <Chip label="India jurisdiction" />
              <Chip label="HTTPS enabled" />
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* SUMMARY CARDS */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Summary
          </Typography>
          <Typography color="text.secondary">
            This page is a summary for convenience. For full details, please refer to the linked policies.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {points.map((p) => (
            <Grid item xs={12} md={6} key={p.title}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center" }}>{p.icon}</Box>
                    <Typography variant="h6" fontWeight={800}>
                      {p.title}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    {p.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* COMPANY DETAILS */}
      <Box sx={{ py: { xs: 5, md: 7 }, bgcolor: "action.hover" }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h5" fontWeight={900} gutterBottom>
                    Company information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={1.25}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Company:</strong> BhamuIntelli Systems LLP
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>LLPIN:</strong> ACR-5927
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Registered Address:</strong> SNO 5/2/1 Daffodils, Gulmohar City, Kharadi,
                      Viman Nagar, Pune 411014, Maharashtra, India
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Email:</strong> bis9.llp@gmail.com, neha@studytunnel.com
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong> +91 9096272737
                    </Typography>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" color="text.secondary">
                    For official documentation (e.g., PAN/TAN) required during contracting, details are shared
                    on request with verified parties.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <ContactMailIcon />
                    <Typography variant="h6" fontWeight={900}>
                      Grievance Redressal
                    </Typography>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    For privacy or data-related concerns, contact our Grievance Officer.
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="body2" color="text.secondary">
                    <strong>Grievance Officer:</strong> Authorized Representative<br />
                    <strong>Email:</strong> bis9.llp@gmail.com, neha@studytunnel.com<br />
                    <strong>Response Time:</strong> Within 7 working days<br />
                    <strong>Jurisdiction:</strong> India
                  </Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>                   
                    <Button variant="outlined" component={RouterLink} to="/privacy-policy">
                      View Privacy Policy
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Card variant="outlined" sx={{ borderRadius: 3, p: { xs: 3, md: 4 }, textAlign: "center" }}>
          <Typography variant="h4" component="h2" fontWeight={900} gutterBottom>
            Need more details?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            If you have compliance questions or require contractual documentation, please contact us.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button variant="contained" component={RouterLink} to="/contact-us">
              Contact Us
            </Button>
            <Button variant="outlined" component={RouterLink} to="/terms-of-service">
              Terms of Service
            </Button>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
};

export default LegalCompliance;
